import { ParsedChord } from '../../types';
import { NOTES, SHARP_NOTES } from './noteUtils';

// Chord intervals for each quality
const CHORD_INTERVALS: { [key: string]: number[] } = {
  '': [0, 4, 7], 'maj': [0, 4, 7], 'm': [0, 3, 7], '°': [0, 3, 6], 'dim': [0, 3, 6], '+': [0, 4, 8], 'aug': [0, 4, 8], 'sus4': [0, 5, 7], 'sus2': [0, 2, 7],
  'maj7': [0, 4, 7, 11], 'm7': [0, 3, 7, 10], '7': [0, 4, 7, 10], 'm(maj7)': [0, 3, 7, 11], '°7': [0, 3, 6, 9], 'dim7': [0, 3, 6, 9], 'ø7': [0, 3, 6, 10], '7sus4': [0, 5, 7, 10], '7sus': [0, 5, 7, 10],
  '6': [0, 4, 7, 9], 'm6': [0, 3, 7, 9],
  'maj9': [0, 4, 7, 11, 14], 'm9': [0, 3, 7, 10, 14], '9': [0, 4, 7, 10, 14], '6/9': [0, 4, 7, 9, 14], 'add9': [0, 4, 7, 14],
  'm11': [0, 3, 7, 10, 14, 17],
  '13': [0, 4, 7, 10, 14, 21],
  '7#5': [0, 4, 8, 10], 'alt': [0, 4, 6, 10], 
};

// Human-readable chord quality names
const CHORD_QUALITY_NAMES: { [key: string]: string } = {
  '': 'Major', 'maj': 'Major', 'm': 'Minor', '°': 'Diminished', 'dim': 'Diminished', '+': 'Augmented', 'aug': 'Augmented', 'sus4': 'Suspended Fourth', 'sus2': 'Suspended Second',
  'maj7': 'Major Seventh', 'm7': 'Minor Seventh', '7': 'Dominant Seventh', 'm(maj7)': 'Minor-Major Seventh', '°7': 'Diminished Seventh', 'dim7': 'Diminished Seventh', 'ø7': 'Half-Diminished Seventh', '7sus4': 'Suspended Dominant Seventh', '7sus': 'Suspended Dominant Seventh',
  '6': 'Major Sixth', 'm6': 'Minor Sixth',
  'maj9': 'Major Ninth', 'm9': 'Minor Ninth', '9': 'Dominant Ninth', '6/9': 'Six Nine', 'add9': 'Add Ninth',
  'm11': 'Minor Eleventh',
  '13': 'Dominant Thirteenth',
  '7#5': 'Augmented Dominant Seventh', 'alt': 'Altered Dominant', 
};

// Alterations that modify intervals
const ALTERATIONS: { [key: string]: { degree: number; change: number } } = {
  'b5': { degree: 5, change: -1 }, '#5': { degree: 5, change: 1 }, 'b9': { degree: 9, change: -1 },
  '#9': { degree: 9, change: 1 }, '#11': { degree: 11, change: 1 }, 'b13': { degree: 13, change: -1 },
};

export const parseChordToNotes = (chordString: string): ParsedChord => {
  if (!chordString) return { notes: null, bassNote: null, error: null, descriptiveName: null, rootNote: null };
  
  const sanitizedChord = chordString.replace(/♯/g, '#').replace(/♭/g, 'b').trim();
  if (!sanitizedChord) return { notes: null, bassNote: null, error: null, descriptiveName: null, rootNote: null };
  
  let mainChordStr = sanitizedChord;
  let bassNoteStr: string | null = null;
  const lastSlashIndex = sanitizedChord.lastIndexOf('/');

  // A valid bass note must come after a slash and be a recognized note name.
  if (lastSlashIndex > 0) {
    const potentialBass = sanitizedChord.substring(lastSlashIndex + 1).trim();
    const bassMatch = potentialBass.match(/^[A-G][b#]?$/);
    // Ensure it's a valid note, not something like '9' from '6/9'
    if (bassMatch && NOTES[bassMatch[0]] !== undefined) {
      mainChordStr = sanitizedChord.substring(0, lastSlashIndex);
      bassNoteStr = potentialBass;
    }
  }

  const rootMatch = mainChordStr.match(/^[A-G][b#]?/);
  if (!rootMatch) return { notes: null, bassNote: null, error: `Invalid root note.`, descriptiveName: null, rootNote: null };
  
  const rootName = rootMatch[0];
  const rootIndex = NOTES[rootName];
  if (rootIndex === undefined) return { notes: null, bassNote: null, error: `Unrecognized root note.`, descriptiveName: null, rootNote: null };

  const remaining = mainChordStr.substring(rootName.length).trim();
  const alterationMatch = remaining.match(/\((.*?)\)/);
  const alterationsStr = alterationMatch ? alterationMatch[1] : '';
  const qualityStr = (alterationMatch ? remaining.replace(alterationMatch[0], '') : remaining).trim();

  const quality = Object.keys(CHORD_INTERVALS).filter(q => qualityStr.startsWith(q)).sort((a, b) => b.length - a.length)[0];
  if (quality === undefined) return { notes: null, bassNote: null, error: `Unrecognized quality.`, descriptiveName: null, rootNote: null };
  
  let intervals = [...(CHORD_INTERVALS[quality] || [])];
  
  const applyAlteration = (token: string) => {
    if (ALTERATIONS[token]) {
      const { degree, change } = ALTERATIONS[token];
      if (degree === 5 && intervals.length >= 3) intervals[2] += change;
      if (degree === 9) intervals.push(14 + change);
      if (degree === 11) intervals.push(17 + change);
      if (degree === 13) intervals.push(21 + change);
    }
  };

  if (alterationsStr) {
    alterationsStr.split(',').map(s => s.trim()).forEach(applyAlteration);
  }
  
  let qualityRemaining = qualityStr.substring(quality.length);
  Object.keys(ALTERATIONS).sort((a,b) => b.length - a.length).forEach(alter => {
    if (qualityRemaining.includes(alter)) {
      applyAlteration(alter);
      qualityRemaining = qualityRemaining.replace(alter, '');
    }
  });

  if (qualityRemaining.trim()) {
    return { notes: null, bassNote: null, error: `Unrecognized symbols.`, descriptiveName: null, rootNote: null };
  }
  
  const noteIndices = intervals.map(interval => rootIndex + interval);
  const notes = noteIndices.map(index => SHARP_NOTES[index % 12].replace('♯', '#'));

  const bassNote: string | null = bassNoteStr;

  const qualityName = CHORD_QUALITY_NAMES[quality] || '';
  let descriptiveName = `${rootName.replace('#', '♯').replace('b', '♭')} ${qualityName}`;
  if (alterationsStr) {
    descriptiveName += ` (${alterationsStr.replace('#', '♯').replace('b', '♭')})`;
  }
  if (bassNote) {
    descriptiveName += ` / ${bassNote.replace('#', '♯').replace('b', '♭')}`;
  }

  return { notes, bassNote, error: null, descriptiveName, rootNote: rootName };
};
